import { useCallback } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useTenant } from "@src/features/tenants";
import { setLastUsedTenantIdSync } from "@src/features/tenants/last-used-tenant-store";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { CameraSearchResult } from "@visioni/a3s-dir/app";

interface CameraSearchResultsProps {
  data: CameraSearchResult | undefined;
  isFetching: boolean;
}

export function CameraSearchResults({ data, isFetching }: CameraSearchResultsProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const { selectedTenant } = useTenant();

  const handleResultPress = useCallback(
    (assetCameraKey: string) => {
      if (!data?.result) return;
      const { tenant, site } = data.result;
      const cameraPath = `/tenants/${tenant}/sites/${site}/cameras/${assetCameraKey}`;
      if (selectedTenant?.id !== tenant) {
        setLastUsedTenantIdSync(tenant);
        const basePathPrefix =
          window.location.pathname === "/app" || window.location.pathname.startsWith("/app/") ? "/app" : "";
        window.location.pathname = `${basePathPrefix}${cameraPath}`;
      } else {
        router.push(cameraPath as never);
      }
    },
    [data, router, selectedTenant],
  );

  if (isFetching) {
    return (
      <View style={styles.dropdown}>
        <View style={[styles.item, styles.itemCenter]}>
          <ActivityIndicator size="small" color={theme.colors.textMuted} />
        </View>
      </View>
    );
  }

  if (data?.result && data.result.cameras.length > 0) {
    return (
      <View style={styles.dropdown}>
        {data.result.cameras.map((cam) => (
          <Pressable key={cam.assetCameraKey} onPress={() => handleResultPress(cam.assetCameraKey)} style={styles.item}>
            <Icon name="video" size={16} color={theme.colors.icon} />
            <View style={styles.resultText}>
              <Text textStyle="sm/medium" color="text" numberOfLines={1}>
                {cam.friendlyName ?? cam.camera}
              </Text>
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {cam.assetCameraKey}
              </Text>
            </View>
            <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
          </Pressable>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.dropdown}>
      <View style={styles.item}>
        <Text textStyle="xs/regular" color="textMuted">
          No camera found
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  dropdown: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    marginTop: theme.spacing.xs,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
    overflow: "hidden",
    zIndex: 1000,
    ...theme.shadows.md,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    minHeight: 40,
  },
  itemCenter: {
    justifyContent: "center",
  },
  resultText: {
    flex: 1,
    gap: 2,
  },
}));
